/**
 * A lookup that holds several records — the reference's chip multi-select
 * (BILL-005, BILL-034): "Purchase orders: PO#4041 × PO#4052 ×".
 *
 * Each pick is added as a chip and the search box empties for the next one.
 * The value is an array of ids; a document saved while the field held a single
 * id still opens, as a one-chip list.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { idsOf } from '@project/contracts'
import { useApi } from '../app/api.js'
import { Lookup } from '../design/index.js'

const Chip = ({ entity, id, known, disabled, onRemove }: {
  entity: string
  id: string
  known: string | undefined
  disabled: boolean | undefined
  onRemove: () => void
}) => {
  const api = useApi()
  const row = useQuery({
    queryKey: ['lookupCard', entity, id],
    queryFn: async () => (await api.lookupById(entity, id)) ?? null,
    enabled: known === undefined,
    staleTime: 60_000,
  })
  const label = known ?? row.data?.label ?? '…'
  return disabled ? (
    <span className="chip">{label}</span>
  ) : (
    <button type="button" className="chip chip--removable" onClick={onRemove} aria-label={`Remove ${label}`}>
      {label} <span aria-hidden="true">×</span>
    </button>
  )
}

export interface MultiLookupProps {
  readonly entity: string
  readonly value: unknown
  readonly onChange: (ids: string[]) => void
  readonly disabled?: boolean | undefined
  readonly invalid?: boolean | undefined
  readonly params?: Readonly<Record<string, string>> | undefined
  readonly browse?: boolean | undefined
  readonly emptyMessage?: string | undefined
}

export const MultiLookup = ({ entity, value, onChange, disabled, invalid, params, browse, emptyMessage }: MultiLookupProps) => {
  const ids = idsOf(value)
  const [labels, setLabels] = useState<Record<string, string>>({})
  // Remounted after every pick, so the box is empty for the next one.
  const [round, setRound] = useState(0)

  return (
    <div className="stack" style={{ gap: 8 }}>
      {ids.length > 0 ? (
        <div className="row row--wrap" style={{ gap: 6 }} aria-label="Chosen">
          {ids.map((id) => (
            <Chip key={id} entity={entity} id={id} known={labels[id]} disabled={disabled}
              onRemove={() => onChange(ids.filter((x) => x !== id))} />
          ))}
        </div>
      ) : null}
      {disabled ? null : (
        <Lookup
          key={round}
          entity={entity}
          value=""
          invalid={invalid}
          placeholder={ids.length > 0 ? 'Add another' : 'Please start typing to search'}
          {...(params ? { params } : {})}
          {...(browse ? { browse } : {})}
          {...(emptyMessage !== undefined ? { emptyMessage } : {})}
          onChange={(next, option) => {
            if (next !== '' && !ids.includes(next)) {
              if (option) setLabels((prev) => ({ ...prev, [next]: option.label }))
              onChange([...ids, next])
            }
            setRound((r) => r + 1)
          }}
        />
      )}
    </div>
  )
}
